import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  LayoutDashboard, BookOpen, Settings, ArrowLeft, Plus, Trash2,
  Clock, Save, Loader2, Package, Send, Copy, CheckCircle2,
  User, Mail, Phone, Calendar, Video, Music, IndianRupee, DollarSign,
  Globe, MapPin, AlertCircle, Info
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Pricing data from NSM course structure
const SUBCONTINENT_COUNTRIES = ['india', 'nepal', 'sri lanka', 'bangladesh', 'pakistan', 'bhutan', 'maldives'];

const PRICING = {
  domestic: {
    label: 'Subcontinent',
    currency: '₹',
    currencyCode: 'INR',
    packages: {
      32: { price: 20000, gst: true },
      40: { price: 25000, gst: true },
      50: { price: 31250, gst: true },
      65: { price: 40625, gst: true },
    },
    note: '+18% GST applicable',
  },
  international: {
    label: 'International',
    currency: '$',
    currencyCode: 'USD',
    packages: {
      32: { price: 420 },
      40: { price: 525 },
      50: { price: 656 },
      65: { price: 853 },
    },
    note: 'PayPal / International Transfer',
  },
};

// Per-hour rate derived from base: ₹20,000/32hrs = ₹625/hr, $420/32hrs = $13.13/hr
const getPerHourRate = (region) => {
  return region === 'domestic'
    ? { rate: 625, currency: '₹' }
    : { rate: 13.13, currency: '$' };
};

const getStudentRegion = (student) => {
  const country = (student?.country || '').toLowerCase().trim();
  return SUBCONTINENT_COUNTRIES.some(c => country.includes(c)) ? 'domestic' : 'international';
};

const formatPrice = (amount, currency) => {
  if (currency === '₹') return `₹${amount.toLocaleString('en-IN')}`;
  return `$${amount.toLocaleString('en-US')}`;
};

const AdminSidebar = () => {
  return (
    <aside className="fixed left-0 top-0 h-screen w-64 admin-sidebar border-r border-[#2A2A2A] pt-20 hidden lg:block">
      <nav className="p-4 space-y-2">
        <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <LayoutDashboard size={20} />
          <span>Dashboard</span>
        </Link>
        <Link to="/admin/courses" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <BookOpen size={20} />
          <span>Course Guide</span>
        </Link>
        <Link to="/admin/settings" className="flex items-center gap-3 px-4 py-3 rounded-lg text-[#A1A1AA] hover:bg-white/5 hover:text-[#F5F5DC] transition-colors">
          <Settings size={20} />
          <span>Class Settings</span>
        </Link>
      </nav>
    </aside>
  );
};

export const AdminPackageBuilder = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [classes, setClasses] = useState([]);
  const [hourPackages, setHourPackages] = useState([]);
  const [existingPackage, setExistingPackage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const [packageData, setPackageData] = useState({
    total_hours: 40,
    semester_months: 6,
    class_allocations: [],
    notes: ''
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const fetchData = async () => {
    try {
      const [studentRes, classesRes, packagesRes] = await Promise.all([
        axios.get(`${API}/students/${studentId}`),
        axios.get(`${API}/classes`),
        axios.get(`${API}/hour-packages`)
      ]);
      
      setStudent(studentRes.data);
      setClasses(classesRes.data);
      setHourPackages(packagesRes.data);

      // Check for existing package
      try {
        const existingRes = await axios.get(`${API}/packages/student/${studentId}`);
        setExistingPackage(existingRes.data);
        setPackageData({
          total_hours: existingRes.data.total_hours,
          semester_months: existingRes.data.semester_months,
          class_allocations: existingRes.data.class_allocations || [],
          notes: existingRes.data.notes || ''
        });
      } catch (e) {
        // No existing package, pre-fill from student's class interests
        if (studentRes.data.class_interests?.length > 0) {
          const prefilled = studentRes.data.class_interests.map(ci => ({
            class_id: ci.class_id,
            class_name: ci.class_name,
            classroom_name: ci.classroom_name,
            hours_allocated: 2,
            sessions_per_month: 2,
            delivery_mode: ci.prefer_recording ? 'recording' : 'both',
            notes: ''
          }));
          setPackageData(prev => ({
            ...prev,
            class_allocations: prefilled
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const addClassAllocation = () => {
    setPackageData(prev => ({
      ...prev,
      class_allocations: [...prev.class_allocations, {
        class_id: '',
        class_name: '',
        classroom_name: '',
        hours_allocated: 2,
        sessions_per_month: 2,
        delivery_mode: 'both',
        notes: ''
      }]
    }));
  };

  const updateAllocation = (index, field, value) => {
    setPackageData(prev => ({
      ...prev,
      class_allocations: prev.class_allocations.map((alloc, i) => {
        if (i !== index) return alloc;
        
        if (field === 'class_id') {
          const selectedClass = classes.find(c => c.id === value);
          return {
            ...alloc,
            class_id: value,
            class_name: selectedClass?.name || '',
            classroom_name: selectedClass?.classroom_name || ''
          };
        }
        
        return { ...alloc, [field]: value };
      })
    }));
  };

  const removeAllocation = (index) => {
    setPackageData(prev => ({
      ...prev,
      class_allocations: prev.class_allocations.filter((_, i) => i !== index)
    }));
  };

  const totalAllocatedHours = packageData.class_allocations.reduce(
    (sum, alloc) => sum + (parseFloat(alloc.hours_allocated) || 0), 0
  );

  const handleSave = async () => {
    if (packageData.class_allocations.length === 0) {
      toast.error('Please add at least one class');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        student_id: studentId,
        ...packageData
      };

      if (existingPackage) {
        await axios.put(`${API}/packages/${existingPackage.id}`, payload);
        toast.success('Package updated');
      } else {
        const response = await axios.post(`${API}/packages`, payload);
        setExistingPackage(response.data.package);
        toast.success('Package created');
      }
    } catch (error) {
      console.error('Error saving package:', error);
      toast.error('Failed to save package');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!existingPackage) {
      toast.error('Please save the package first');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/packages/${existingPackage.id}/send`);
      toast.success('Package marked as sent! Share the link with the student.');
      setExistingPackage(prev => ({ ...prev, sent_at: response.data.sent_at }));
    } catch (error) {
      console.error('Error sending package:', error);
      toast.error('Failed to send package');
    } finally {
      setSending(false);
    }
  };

  const copyLink = () => {
    if (existingPackage) {
      const link = `${window.location.origin}/view-package/${existingPackage.id}`;
      navigator.clipboard.writeText(link);
      setLinkCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setLinkCopied(false), 3000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D4AF37]" size={40} />
      </div>
    );
  }

  if (!student) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="text-[#A1A1AA]">Student not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header variant="solid" />
      <AdminSidebar />
      
      <main className="lg:ml-64 pt-20 p-6">
        <div className="max-w-5xl mx-auto">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate('/admin')}
            className="mb-6 text-[#A1A1AA] hover:text-[#EDEDED]"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to Dashboard
          </Button>

          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl text-[#EDEDED] mb-2">Package Builder</h1>
              <p className="text-[#A1A1AA]">Create a custom semester plan for this student</p>
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="btn-gold"
                data-testid="btn-save-package"
              >
                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                Save Package
              </Button>
              
              {existingPackage && (
                <>
                  <Button
                    onClick={handleSend}
                    disabled={sending}
                    variant="outline"
                    className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
                    data-testid="btn-send-package"
                  >
                    {sending ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send size={16} className="mr-2" />}
                    {existingPackage.sent_at ? 'Resend' : 'Mark as Sent'}
                  </Button>
                  
                  <Button
                    onClick={copyLink}
                    variant="outline"
                    className="border-white/20 text-[#EDEDED]"
                    data-testid="btn-copy-link"
                  >
                    {linkCopied ? <CheckCircle2 size={16} className="mr-2 text-green-400" /> : <Copy size={16} className="mr-2" />}
                    Copy Link
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Student Info Card */}
          <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-6 mb-8">
            <h2 className="font-heading text-xl text-[#F5F5DC] mb-4">Student Information</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="flex items-center gap-3">
                <User size={18} className="text-[#D4AF37]" />
                <div>
                  <p className="text-xs text-[#A1A1AA]">Name</p>
                  <p className="text-[#EDEDED]">{student.full_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={18} className="text-[#D4AF37]" />
                <div>
                  <p className="text-xs text-[#A1A1AA]">Email</p>
                  <p className="text-[#EDEDED]">{student.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge className={student.student_type === 'new' ? 'bg-emerald-500/20 text-emerald-400' : student.student_type === 'returning' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}>
                  {student.student_type}
                </Badge>
              </div>
            </div>
            
            {student.class_interests?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[#2A2A2A]">
                <p className="text-xs text-[#A1A1AA] mb-2">Student's Requested Classes</p>
                <div className="flex flex-wrap gap-2">
                  {student.class_interests.map((ci, idx) => (
                    <span key={idx} className="px-2 py-1 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] text-xs">
                      {ci.class_name} {ci.prefer_recording && '(Recording)'}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Pricing Reference Panel */}
          {(() => {
            const region = getStudentRegion(student);
            const pricing = PRICING[region];
            const perHour = getPerHourRate(region);
            const selectedPkg = pricing.packages[packageData.total_hours];
            const isSubcontinent = region === 'domestic';

            return (
              <div className={`border rounded-xl p-6 mb-8 ${isSubcontinent ? 'bg-orange-500/5 border-orange-500/20' : 'bg-blue-500/5 border-blue-500/20'}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {isSubcontinent ? (
                      <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center">
                        <IndianRupee className="text-orange-400" size={20} />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                        <DollarSign className="text-blue-400" size={20} />
                      </div>
                    )}
                    <div>
                      <h2 className="font-heading text-xl text-[#F5F5DC]">Pricing Reference</h2>
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin size={12} className="text-[#A1A1AA]" />
                        <span className="text-[#A1A1AA]">
                          {student.city && `${student.city}, `}{student.country || 'Unknown'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isSubcontinent ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'}`}>
                          {pricing.label}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-[#A1A1AA]">Per Hour Rate</p>
                    <p className="font-heading text-lg text-[#D4AF37]">
                      {formatPrice(perHour.rate, perHour.currency)}/hr
                    </p>
                  </div>
                </div>

                {/* Package pricing grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  {Object.entries(pricing.packages).map(([hours, pkg]) => {
                    const isSelected = packageData.total_hours === parseInt(hours);
                    return (
                      <div
                        key={hours}
                        className={`p-3 rounded-lg border text-center transition-all cursor-pointer ${
                          isSelected
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                            : 'border-[#2A2A2A] bg-black/20 hover:border-[#D4AF37]/50'
                        }`}
                        onClick={() => setPackageData(prev => ({ ...prev, total_hours: parseInt(hours) }))}
                      >
                        <p className="text-xs text-[#A1A1AA]">{hours} Hours</p>
                        <p className={`font-heading text-lg ${isSelected ? 'text-[#D4AF37]' : 'text-[#EDEDED]'}`}>
                          {formatPrice(pkg.price, pricing.currency)}
                        </p>
                        {pkg.gst && <p className="text-[10px] text-orange-400/70">+GST</p>}
                      </div>
                    );
                  })}
                </div>

                {/* Selected package details */}
                {selectedPkg && (
                  <div className="flex items-center justify-between bg-black/30 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <Info size={14} className="text-[#A1A1AA]" />
                      <span className="text-sm text-[#A1A1AA]">
                        {packageData.total_hours}hr package for this student:
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="font-heading text-xl text-[#D4AF37]">
                        {formatPrice(selectedPkg.price, pricing.currency)}
                      </span>
                      {selectedPkg.gst && (
                        <span className="text-xs text-orange-400 ml-2">
                          ({formatPrice(Math.round(selectedPkg.price * 1.18), pricing.currency)} incl. GST)
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <p className="text-xs text-[#A1A1AA] mt-3 flex items-center gap-1">
                  <Globe size={10} />
                  {pricing.note}
                  {!selectedPkg && packageData.total_hours === 24 && (
                    <span className="text-yellow-400 ml-2">
                      — 24hr is a custom package. Price: {formatPrice(Math.round(perHour.rate * 24), perHour.currency)}
                    </span>
                  )}
                </p>
              </div>
            );
          })()}

          {/* Package Configuration */}
          <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-6 mb-8">
            <h2 className="font-heading text-xl text-[#F5F5DC] mb-4">Package Configuration</h2>

            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">Total Hours</Label>
                <Select 
                  value={String(packageData.total_hours)}
                  onValueChange={(value) => setPackageData(prev => ({ ...prev, total_hours: parseInt(value) }))}
                >
                  <SelectTrigger className="bg-black/50 border-[#2A2A2A] text-[#EDEDED]" data-testid="select-total-hours">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                    {hourPackages.map(pkg => (
                      <SelectItem key={pkg.id} value={String(pkg.hours)} className="text-[#EDEDED]">
                        {pkg.hours} Hours
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">Semester Duration</Label>
                <Select 
                  value={String(packageData.semester_months)}
                  onValueChange={(value) => setPackageData(prev => ({ ...prev, semester_months: parseInt(value) }))}
                >
                  <SelectTrigger className="bg-black/50 border-[#2A2A2A] text-[#EDEDED]" data-testid="select-months">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                    {[3, 4, 5, 6, 9, 12].map(months => (
                      <SelectItem key={months} value={String(months)} className="text-[#EDEDED]">
                        {months} Months
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#A1A1AA]">Hours Allocated</Label>
                <div className={`p-3 rounded-lg border ${totalAllocatedHours > packageData.total_hours ? 'border-red-500 bg-red-500/10' : 'border-[#2A2A2A] bg-black/30'}`}>
                  <span className={`font-heading text-2xl ${totalAllocatedHours > packageData.total_hours ? 'text-red-400' : 'text-[#D4AF37]'}`}>
                    {totalAllocatedHours}
                  </span>
                  <span className="text-[#A1A1AA]"> / {packageData.total_hours} hours</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#A1A1AA]">Notes for Student</Label>
              <Textarea
                value={packageData.notes}
                onChange={(e) => setPackageData(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-black/50 border-[#2A2A2A] text-[#EDEDED]"
                placeholder="Any notes about this package..."
                data-testid="textarea-notes"
              />
            </div>
          </div>

          {/* Class Allocations */}
          <div className="bg-[#121212] border border-[#2A2A2A] rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl text-[#F5F5DC]">Class Allocations</h2>
              <Button
                onClick={addClassAllocation}
                variant="outline"
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
                data-testid="btn-add-class"
              >
                <Plus size={16} className="mr-2" />
                Add Class
              </Button>
            </div>

            {packageData.class_allocations.length === 0 ? (
              <div className="text-center py-12 text-[#A1A1AA]">
                <Package size={40} className="mx-auto mb-4 opacity-50" />
                <p>No classes added yet. Click "Add Class" to start building the package.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {packageData.class_allocations.map((alloc, index) => (
                  <div key={index} className="border border-[#2A2A2A] rounded-lg p-4 bg-black/20">
                    <div className="grid md:grid-cols-5 gap-4">
                      <div className="md:col-span-2 space-y-2">
                        <Label className="text-[#A1A1AA] text-xs">Class</Label>
                        <Select 
                          value={alloc.class_id}
                          onValueChange={(value) => updateAllocation(index, 'class_id', value)}
                        >
                          <SelectTrigger className="bg-black/50 border-[#2A2A2A] text-[#EDEDED]">
                            <SelectValue placeholder="Select class" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#121212] border-[#2A2A2A] max-h-[300px]">
                            {classes.map(cls => (
                              <SelectItem key={cls.id} value={cls.id} className="text-[#EDEDED]">
                                <span className="flex items-center gap-2">
                                  {cls.class_type === 'compulsory' && (
                                    <span className="text-[9px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded font-medium">REQ</span>
                                  )}
                                  <span>{cls.classroom_name}: {cls.name}</span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-[#A1A1AA] text-xs">Hours</Label>
                        <Input
                          type="number"
                          step="0.5"
                          min="0.5"
                          value={alloc.hours_allocated}
                          onChange={(e) => updateAllocation(index, 'hours_allocated', parseFloat(e.target.value))}
                          className="bg-black/50 border-[#2A2A2A] text-[#EDEDED]"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-[#A1A1AA] text-xs">Sessions/Month</Label>
                        <Select 
                          value={String(alloc.sessions_per_month)}
                          onValueChange={(value) => updateAllocation(index, 'sessions_per_month', parseInt(value))}
                        >
                          <SelectTrigger className="bg-black/50 border-[#2A2A2A] text-[#EDEDED]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                            {[1, 2, 3, 4, 5, 6, 8].map(num => (
                              <SelectItem key={num} value={String(num)} className="text-[#EDEDED]">
                                {num}x
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-[#A1A1AA] text-xs">Delivery</Label>
                        <div className="flex gap-2">
                          <Select 
                            value={alloc.delivery_mode}
                            onValueChange={(value) => updateAllocation(index, 'delivery_mode', value)}
                          >
                            <SelectTrigger className="bg-black/50 border-[#2A2A2A] text-[#EDEDED] flex-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                              <SelectItem value="live" className="text-[#EDEDED]">Live</SelectItem>
                              <SelectItem value="recording" className="text-[#EDEDED]">Recording</SelectItem>
                              <SelectItem value="both" className="text-[#EDEDED]">Both</SelectItem>
                            </SelectContent>
                          </Select>
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeAllocation(index)}
                            className="text-red-400 hover:text-red-400 hover:bg-red-500/10 px-2"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminPackageBuilder;
